<script setup lang="ts">
import { ref, onMounted } from 'vue';
import RadioInput from '../../../../components/ui/form/RadioInput.vue';
import { posStore } from '../../../../stores/posStore';
import GridInput from '../../../../components/ui/form/GridInput.vue';
import { useFormValidation } from '../../../../composables/useFormValidation';
import { required } from '../../../../utils/validation';
import { api } from '../../../../api/Api';
import Toast from '../../../../services/Toast';
import Buttons from '../../../../components/ui/form/Buttons.vue';
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue';
import GroupedSelect from '../../../../components/ui/form/GroupedSelect.vue';
import TextAreaInput from '../../../../components/ui/form/TextAreaInput.vue';
import NumberInput from '../../../../components/ui/form/NumberInput.vue';
import CheckInput from '../../../../components/ui/form/CheckInput.vue';


const pos = posStore();

const delivery_address_id = ref<string | number>()
const remarks = ref('');
const endPoint = "/clients/revenue/master-data/customers/";
const showNewAddressForm = ref(false);
const to_delivery = ref(false);

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
} = useFormValidation({
  apartments_id: "",
  customers_id: "",
  customer: "",
  address: "",
  buildings_id: "",
  primary: false,
  countrys_id: 1,
  states_id: "",
  citys_id: "",
  roads_id: "",
  lng: "",
  lat: "",
  remarks: "",
  active: true,
  uid: "",
});

setFieldRules("customers_id", [required("Customer is required")]);
setFieldRules("countrys_id", [required("Country is required")]);
setFieldRules("states_id", [required("State is required")]);
setFieldRules("citys_id", [required("City is required")]);

const handleSubmit = async () => {
  fields.value.customers_id.value = pos.selectedPosInvoice?.invoice?.customer?.id || "";
  console.log("Form Data:", formData.value);
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      endPoint + 'address-' + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("New Address successfully");
      if (pos.selectedPosInvoice?.invoice?.customer) {
        pos.selectedPosInvoice.invoice.customer.addresses = response.data.data;
      }
      resetForm();
      showNewAddressForm.value = false;
    }
  }
};

const handleAddreSelect = () => {
  if (pos.selectedPosInvoice?.invoice) {
    pos.selectedPosInvoice.invoice.delivery_address_id = delivery_address_id.value;
  }
}
const handleUpdateRemarks = () => {
  console.log("Remarks updated:", remarks.value);
  if (pos.selectedPosInvoice?.invoice) {
    console.log("Updating remarks in invoice");
    pos.selectedPosInvoice.invoice.delivery_remarks = remarks.value;
  }
};
const handleToDeliverChange = () => {
  if (pos.selectedPosInvoice?.invoice) {
    pos.selectedPosInvoice.invoice.to_deliver = to_delivery.value;
  }
};
onMounted(() => {
 resetForm();
  if (pos.selectedPosInvoice?.invoice) {
    to_delivery.value = pos.selectedPosInvoice.invoice.to_deliver || false;
    delivery_address_id.value = pos.selectedPosInvoice.invoice.delivery_address_id || '';
  }
});
</script>

<template>
  <div class="sales-step">
    <div class="p-4 h-full overflow-y-auto pb-20">
      <div class="mb-4">
        <h2 class="text-lg font-semibold text-neutral-900 mb-1">Delivery Information</h2>
        <div class="flex items-center justify-between">
          <p class="text-sm text-neutral-600">Where should we deliver the items?</p>
          <CheckInput
            v-model="to_delivery"
            label="To Deliver"
            @change="handleToDeliverChange"
          />
        </div>
      </div>
      
      <div class="card mb-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="font-medium text-neutral-900">Delivery Address</h3>
          <Buttons
            caption="New Address"
            @click="showNewAddressForm = true"
            class="text-sm"
            icon="House"
          />
        </div>

        <RadioInput
          option-label="full_address"
          name="delivery_address"
          @change="handleAddreSelect"
          label=""
          :options="pos.selectedPosInvoice?.invoice.customer?.addresses || []"
          v-model="delivery_address_id"
        />
        
        <GridInput label="Add New Address" class="mt-3" v-if="showNewAddressForm">
          <div class="grid md:grid-cols-3 gap-6">
            <SearchSelect
              v-model="fields.countrys_id.value"
              label="Country"
              end-point="system/master-data/countrys"
              :error="fields.countrys_id.touched ? fields.countrys_id.error : null"
              @blur="fields.countrys_id.touched = true"
              :allowNewRecord="true"
            />

            <GroupedSelect
              v-model="fields.states_id.value"
              label="State"
              :hasExtra="true"
              optionLabel="states"
              :extra="fields.countrys_id.value"
              end-point="system/master-data/states"
              :error="fields.states_id.touched ? fields.states_id.error : null"
              @blur="fields.states_id.touched = true"
              :allowNewRecord="true"
            />

            <GroupedSelect
              v-model="fields.citys_id.value"
              label="City"
              :hasExtra="true"
              :extra="fields.states_id.value"
              end-point="system/master-data/citys"
              :error="fields.citys_id.touched ? fields.citys_id.error : null"
              optionLabel="citys"
              @blur="fields.citys_id.touched = true"
              :allowNewRecord="true"
            />

            <GroupedSelect
              v-model="fields.roads_id.value"
              label="Road"
              optionLabel="roads"
              :hasExtra="true"
              :extra="fields.citys_id.value"
              end-point="system/master-data/roads"
              :error="fields.roads_id.touched ? fields.roads_id.error : null"
              @blur="fields.roads_id.touched = true"
              :allowNewRecord="true"
            />

            <GroupedSelect
              v-model="fields.buildings_id.value"
              label="Building"
              :hasExtra="true"
              optionLabel="buildings"
              :remote="true"
              :extra="fields.citys_id.value"
              end-point="system/master-data/buildings"
              :error="
                fields.buildings_id.touched ? fields.buildings_id.error : null
              "
              @blur="fields.buildings_id.touched = true"
              :allowNewRecord="true"
            />

            <TextAreaInput
              v-model="fields.address.value"
              label="Address"
              :rows="3"
              :error="fields.address.touched ? fields.address.error : null"
              @blur="fields.address.touched = true"
            />

            <NumberInput
              v-model="fields.lat.value"
              label="Latitude"
              :min="0"
              :error="fields.lat.touched ? fields.lat.error : null"
              @blur="fields.lat.touched = true"
            />

            <NumberInput
              v-model="fields.lng.value"
              label="Longitude"
              :min="0"
              :error="fields.lng.touched ? fields.lng.error : null"
              @blur="fields.lng.touched = true"
            />

          </div>

          <Buttons caption="Save Address" @click="handleSubmit" class="mt-4" icon="Save" />
        </GridInput>

      </div>
      
      <div class="card">
        <h3 class="font-medium text-neutral-900 mb-3">Delivery Instructions</h3>
        
        <div>
          <TextAreaInput
            id="remarks"
            v-model="remarks"
            label="Remarks"
            placeholder="Enter any special delivery instructions..."
            @input="handleUpdateRemarks"
          />
        </div>
      </div>
    </div>
  </div>
</template>